import { computed, ref } from 'vue'
import { useApi } from './useApi'
import { useSeason } from './useSeason'
import type { Matchday, Prediction } from '@/types/api'

/**
 * The round that is open right now, shared across the app.
 *
 * The shell shows it in the sidebar and the Jugar page renders it in full, so
 * it lives here as a singleton: one request, one source of truth, and saving a
 * sheet refreshes both places at once.
 */

const matchday = ref<Matchday | null>(null)
const prediction = ref<Prediction | null>(null)
const loading = ref(false)
const loaded = ref(false)
const error = ref<string | null>(null)
let inFlight: Promise<void> | null = null

export function useCurrentRound() {
    const { getCurrentMatchday } = useApi()
    const { season, fetchSeason } = useSeason()

    async function load(force = false): Promise<void> {
        if (inFlight) return inFlight
        if (loaded.value && !force) return

        inFlight = (async () => {
            loading.value = true
            error.value = null
            try {
                // The season has to be resolved first: useSeason starts its
                // fetch but nothing awaits it, so an unguarded request here
                // would ask for the fallback season's data.
                await fetchSeason()
                const response = await getCurrentMatchday(season.value)
                matchday.value = response.data?.data ?? null
                prediction.value = response.data?.prediction ?? null
            } catch {
                error.value = 'No se ha podido cargar la jornada actual.'
            } finally {
                loading.value = false
                loaded.value = true
                inFlight = null
            }
        })()

        return inFlight
    }

    /** How many of this round's fixtures the player has filled in. */
    const filled = computed(
        () =>
            (prediction.value?.predictions ?? []).filter(
                (entry) => entry.homeScore != null && entry.awayScore != null
            ).length
    )

    const total = computed(() => matchday.value?.matches?.length ?? 0)

    return { matchday, prediction, loading, loaded, error, load, filled, total }
}
